/* =========================================
   SECTION: Cart Drawer (Shopify 2.0)
   
   Full-featured slide-out cart drawer built on
   Web Components and Shopify Section Rendering API.
   
   Features:
   - AJAX add/remove/update with live DOM swap
   - Gift product auto-sync (tag-triggered, abuse-proof)
   - Discount code application
   - Compare-at-price display (handled in Liquid)
   - Cart note
   - Checkout lock during gift sync
   - Comprehensive logging
   
   DEPENDENCIES:
   - cart.js (CartItems, CartRemoveButton, CartNote)
   - global.js (fetchConfig, debounce, trapFocus, removeTrapFocus)
   - window.routes, window.cartStrings
   
   CUSTOM EVENTS EMITTED:
   - cart:updated  — after any cart modification
   - cart:opened   — when drawer opens
   - cart:closed   — when drawer closes
   - cart:gift-sync-start  — gift sync begins
   - cart:gift-sync-end    — gift sync completes
========================================= */

"use strict";

/* =========================================
   SECTION: CartDrawer Custom Element
   Main container for the sliding cart drawer.
   Implements the interface expected by product-form.js:
   - getSectionsToRender()
   - renderContents(parsedState)
   - setActiveElement(element)
========================================= */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this._giftSyncInProgress = false;
    this._giftSyncLock = false;
    this._animating = false;

    /* Bind overlay click to close */
    this.bindOverlayClose();

    /* Setup header cart icon as drawer trigger */
    this.setupCartIconTrigger();
  }

  /* -----------------------------------------
     SUBSECTION: Event Binding
  ----------------------------------------- */

  bindOverlayClose() {
    const overlay = this.querySelector("#CartDrawer-Overlay");
    if (overlay) {
      overlay.addEventListener("click", () => this.close());
    }
  }

  setupCartIconTrigger() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");

    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.refreshAndOpen();
    });
  }

  /**
   * Fetches fresh cart sections and opens the drawer.
   * Used when clicking the header cart icon (no parsedState available).
   */
  async refreshAndOpen() {
    CartLogger.logOperation("refreshAndOpen");

    try {
      const sectionIds = this.getSectionsToRender().map((s) => s.id);
      const response = await fetch(
        `${window.location.pathname}?sections=${sectionIds.join(",")}`
      );
      const sections = await response.json();

      this.updateSectionsHTML(sections);
      this.open();
      this.runGiftSync();
    } catch (error) {
      CartLogger.error("refreshAndOpen failed:", error);
      /* Fallback: open with current (possibly stale) content */
      this.open();
    }
  }

  /* -----------------------------------------
     SUBSECTION: Open / Close
  ----------------------------------------- */

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    CartLogger.logOperation("open");

    /* Setup note summary accessibility if present */
    const noteSummary = this.querySelector("[id^='Details-'] summary");
    if (noteSummary && !noteSummary.hasAttribute("role")) {
      this.setSummaryAccessibility(noteSummary);
    }

    /* Prevent body scroll */
    document.documentElement.style.overflow = "hidden";
    document.body.classList.add("overflow-hidden");

    /* Animate in */
    requestAnimationFrame(() => {
      this.classList.add("active", "animate");
    });

    /* Trap focus after animation completes */
    this.addEventListener(
      "transitionend",
      () => {
        const container = this.classList.contains("is-empty")
          ? this.querySelector(".cart-drawer__empty")
          : document.getElementById("CartDrawer");
        const focusEl =
          this.querySelector(".cart-drawer__inner") ||
          this.querySelector(".cart-drawer__close");
        if (container) trapFocus(container, focusEl);
      },
      { once: true }
    );

    document.dispatchEvent(new CustomEvent("cart:opened"));
  }

  close() {
    CartLogger.logOperation("close");

    this.classList.remove("active");

    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
    document.documentElement.style.overflow = "";

    document.dispatchEvent(new CustomEvent("cart:closed"));
  }

  /* -----------------------------------------
     SUBSECTION: Section Rendering
     Core of Shopify 2.0 cart updates.
     product-form.js and CartDrawerItems both call
     these methods to update the drawer content.
  ----------------------------------------- */

  /**
   * Returns the sections this drawer needs for rendering.
   * These IDs are sent to Cart API requests so Shopify
   * returns pre-rendered HTML in the response.
   */
  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer"
      },
      {
        id: "cart-icon-bubble",
        selector: ".shopify-section"
      }
    ];
  }

  /**
   * Called by product-form.js after a successful add-to-cart.
   * Receives the parsed JSON response from /cart/add.js which
   * includes pre-rendered section HTML.
   *
   * @param {object} parsedState - response from Cart API with sections
   */
  renderContents(parsedState) {
    CartLogger.logOperation("renderContents", {
      productId: parsedState.id,
      itemCount: parsedState.sections ? "has sections" : "no sections"
    });

    /* Remove empty state if it was showing */
    this.querySelector(".cart-drawer__inner")?.classList.remove("is-empty");
    this.productId = parsedState.id;

    /* Update each section's HTML */
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (sectionElement && parsedState.sections?.[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      }
    });

    /* Re-bind events on fresh DOM */
    this.bindOverlayClose();

    /* Open the drawer and sync gifts */
    setTimeout(() => {
      this.open();
      this.runGiftSync();
    }, 0);
  }

  /**
   * Updates sections HTML from a sections response object.
   * Used by refreshAndOpen() and gift sync re-fetch.
   */
  updateSectionsHTML(sections) {
    this.getSectionsToRender().forEach((section) => {
      const el = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (el && sections[section.id]) {
        el.innerHTML = this.getSectionInnerHTML(
          sections[section.id],
          section.selector
        );
      }
    });

    /* Re-bind overlay after DOM swap */
    this.bindOverlayClose();

    /* Update empty state */
    const emptyAttr = this.querySelector("[data-cart-empty]");
    this.classList.toggle(
      "is-empty",
      emptyAttr?.dataset.cartEmpty === "true"
    );
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector)?.innerHTML || "";
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  setSummaryAccessibility(summary) {
    summary.setAttribute("role", "button");
    summary.setAttribute("aria-expanded", "false");

    if (summary.nextElementSibling?.id) {
      summary.setAttribute("aria-controls", summary.nextElementSibling.id);
    }

    summary.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });
  }

  /* -----------------------------------------
     SUBSECTION: Gift Sync Controller
     
     Reads gift rules from a JSON config element
     rendered by Liquid inside the drawer.
     Compares desired state with actual cart,
     then adds/removes gifts via Cart API.
     
     ABUSE PREVENTION:
     - Gifts are capped at max quantity (default 1)
     - Gifts are auto-removed when trigger is gone
     - Checkout is locked during sync operations
     - Lock has auto-expiry to prevent deadlocks
  ----------------------------------------- */

  /**
   * Main entry point for gift synchronization.
   * Called after every cart render/update.
   */
  async runGiftSync() {
    if (this._giftSyncLock) {
      CartLogger.debug("Gift sync skipped — lock active");
      return;
    }

    const config = this.getGiftConfig();
    if (!config || config.length === 0) {
      CartLogger.debug("No gift rules configured");
      return;
    }

    CartLogger.logOperation("giftSync:start", { rules: config.length });
    document.dispatchEvent(new CustomEvent("cart:gift-sync-start"));
    this.setCheckoutLock(true);
    this._giftSyncLock = true;

    try {
      /* Fetch actual cart to verify real state */
      const cart = await this.fetchCart();
      const cartVariantIds = new Set(
        cart.items.map((item) => item.variant_id)
      );

      const cartItemsByVariant = {};
      cart.items.forEach((item) => {
        cartItemsByVariant[item.variant_id] = item;
      });

      let needsRefresh = false;

      for (const rule of config) {
        const giftVariantId = parseInt(rule.giftVariantId);
        if (!giftVariantId) continue;

        const giftInCart = cartVariantIds.has(giftVariantId);
        const giftItem = cartItemsByVariant[giftVariantId];
        const maxQty = parseInt(rule.maxQty) || 1;

        /* CASE 1: Trigger active, gift NOT in cart → ADD */
        if (rule.triggerActive && !giftInCart) {
          CartLogger.info(
            `Gift sync: Adding gift (variant ${giftVariantId})`
          );
          await this.cartRequest("/cart/add.js", {
            items: [{ id: giftVariantId, quantity: 1 }]
          });
          needsRefresh = true;
        }

        /* CASE 2: No trigger, gift IS in cart → REMOVE */
        if (!rule.triggerActive && giftInCart) {
          CartLogger.info(
            `Gift sync: Removing orphaned gift (variant ${giftVariantId})`
          );
          await this.cartRequest("/cart/change.js", {
            id: giftItem.key,
            quantity: 0
          });
          needsRefresh = true;
        }

        /* CASE 3: Gift in cart but qty exceeds max → CAP */
        if (giftInCart && giftItem.quantity > maxQty) {
          CartLogger.info(
            `Gift sync: Capping gift qty to ${maxQty} (variant ${giftVariantId})`
          );
          await this.cartRequest("/cart/change.js", {
            id: giftItem.key,
            quantity: maxQty
          });
          needsRefresh = true;
        }
      }

      /* Re-render sections if cart was modified */
      if (needsRefresh) {
        CartLogger.debug("Gift sync: refreshing sections after changes");
        const sectionIds = this.getSectionsToRender().map((s) => s.id);
        const res = await fetch(
          `${window.location.pathname}?sections=${sectionIds.join(",")}`
        );
        const sections = await res.json();
        this.updateSectionsHTML(sections);
      }
    } catch (error) {
      CartLogger.error("Gift sync failed:", error);
    } finally {
      this._giftSyncLock = false;
      this.setCheckoutLock(false);
      document.dispatchEvent(new CustomEvent("cart:gift-sync-end"));
      CartLogger.logOperation("giftSync:end");
    }
  }

  /**
   * Reads gift rule configuration from a Liquid-rendered
   * JSON script element inside the drawer.
   */
  getGiftConfig() {
    const el = this.querySelector("#CartDrawer-GiftConfig");
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      CartLogger.error("Failed to parse gift config:", e);
      return null;
    }
  }

  /**
   * Locks or unlocks the checkout button.
   * While locked, a loading spinner is shown.
   */
  setCheckoutLock(locked) {
    const btn = this.querySelector("[data-checkout-btn]");
    if (!btn) return;

    if (locked) {
      btn.setAttribute("disabled", "disabled");
      btn.classList.add("cart-drawer__checkout--syncing");
      CartLogger.debug("Checkout LOCKED (gift sync in progress)");
    } else {
      btn.removeAttribute("disabled");
      btn.classList.remove("cart-drawer__checkout--syncing");
      CartLogger.debug("Checkout UNLOCKED");
    }
  }

  /* -----------------------------------------
     SUBSECTION: Cart API Helpers
  ----------------------------------------- */

  fetchCart() {
    return fetch("/cart.js", {
      method: "GET",
      headers: { "Content-Type": "application/json" }
    }).then((res) => res.json());
  }

  cartRequest(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }).then((res) => {
      if (!res.ok) throw new Error(`${url} failed: ${res.status}`);
      return res.json();
    });
  }

  /* -----------------------------------------
     SUBSECTION: Discount Code
     Applies a discount code by navigating to
     /discount/CODE which sets a Shopify cookie.
     Then refreshes the drawer to show the discount.
  ----------------------------------------- */

  async applyDiscount(code) {
    if (!code || !code.trim()) return;

    CartLogger.logOperation("applyDiscount", { code });
    const cleanCode = code.trim();

    try {
      await fetch(`/discount/${encodeURIComponent(cleanCode)}`, {
        method: "GET"
      });

      /* Refresh drawer to show updated discount state */
      const sectionIds = this.getSectionsToRender().map((s) => s.id);
      const res = await fetch(
        `${window.location.pathname}?sections=${sectionIds.join(",")}`
      );
      const sections = await res.json();
      this.updateSectionsHTML(sections);

      CartLogger.info("Discount applied:", cleanCode);
      return { success: true };
    } catch (error) {
      CartLogger.error("Discount application failed:", error);
      return { success: false, error };
    }
  }

  async clearDiscount() {
    CartLogger.logOperation("clearDiscount");

    try {
      await fetch("/discount/clear", { method: "GET" });

      const sectionIds = this.getSectionsToRender().map((s) => s.id);
      const res = await fetch(
        `${window.location.pathname}?sections=${sectionIds.join(",")}`
      );
      const sections = await res.json();
      this.updateSectionsHTML(sections);

      CartLogger.info("Discount cleared");
    } catch (error) {
      CartLogger.error("Discount clear failed:", error);
    }
  }
}

customElements.define("cart-drawer", CartDrawer);


/* =========================================
   SECTION: CartDrawerItems
   Extends CartItems for drawer-specific behavior.
   Overrides getSectionsToRender() to target
   the drawer DOM instead of the cart page DOM.
   
   After every quantity update, it also triggers
   gift sync on the parent CartDrawer.
========================================= */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".cart-drawer__inner"
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section"
      }
    ];
  }

  /**
   * Override: after quantity update completes,
   * re-bind drawer events and run gift sync.
   */
  updateQuantity(line, quantity, name) {
    CartLogger.logOperation("drawerUpdateQuantity", { line, quantity });
    this.enableLoading(line);

    const sections = this.getSectionsToRender().map((s) => s.section);
    const body = JSON.stringify({
      line,
      quantity,
      sections,
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((res) => res.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        const cartDrawer = document.querySelector("cart-drawer");
        cartDrawer?.classList.toggle(
          "is-empty",
          parsedState.item_count === 0
        );

        /* Update sections */
        this.getSectionsToRender().forEach((section) => {
          const el =
            document.getElementById(section.id)?.querySelector(section.selector) ||
            document.getElementById(section.id);

          if (el && parsedState.sections[section.section]) {
            el.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        /* Re-bind overlay and run gift sync */
        cartDrawer?.bindOverlayClose();
        cartDrawer?.runGiftSync();

        /* Focus management */
        const lineItem =
          document.getElementById(`CartDrawer-Item-${line}`);
        const focusEl = lineItem?.querySelector(`[name="${name}"]`);

        if (focusEl && cartDrawer) {
          trapFocus(cartDrawer, focusEl);
        } else if (parsedState.item_count === 0 && cartDrawer) {
          const emptyZone = cartDrawer.querySelector(".cart-drawer__empty");
          if (emptyZone) trapFocus(cartDrawer, emptyZone.querySelector("a"));
        }

        this.disableLoading();

        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { cart: parsedState, source: "cart-drawer-items" }
          })
        );
      })
      .catch((error) => {
        CartLogger.error("Drawer updateQuantity failed:", error);
        this.querySelectorAll(".loading-overlay").forEach((o) =>
          o.classList.add("hidden")
        );

        const errEl = document.getElementById("CartDrawer-CartErrors");
        if (errEl) errEl.textContent = window.cartStrings.error;

        this.disableLoading();
      });
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);


/* =========================================
   SECTION: Discount Form Handler
   Listens for submit on the discount form
   inside the cart drawer and delegates to
   CartDrawer.applyDiscount().
========================================= */
document.addEventListener("submit", (event) => {
  const form = event.target.closest("[data-discount-form]");
  if (!form) return;

  event.preventDefault();

  const input = form.querySelector("[data-discount-input]");
  const errorEl = form.querySelector("[data-discount-error]");
  const cartDrawer = document.querySelector("cart-drawer");

  if (!input?.value.trim()) {
    if (errorEl) {
      errorEl.textContent = window.cartStrings?.emptyDiscount || "Please enter a code";
      errorEl.style.display = "block";
    }
    return;
  }

  if (errorEl) errorEl.style.display = "none";

  cartDrawer?.applyDiscount(input.value).then((result) => {
    if (result?.success) {
      input.value = "";
    } else if (errorEl) {
      errorEl.textContent = window.cartStrings?.invalidDiscount || "Invalid discount code";
      errorEl.style.display = "block";
    }
  });
});

/* Discount clear button handler */
document.addEventListener("click", (event) => {
  if (!event.target.closest("[data-discount-clear]")) return;
  event.preventDefault();
  document.querySelector("cart-drawer")?.clearDiscount();
});


/* =========================================
   SECTION: Quantity Button Handlers
   Handles +/- quantity buttons in the drawer.
   These are outside the custom element, so we
   use event delegation on the document.
========================================= */
document.addEventListener("click", (event) => {
  const btn = event.target.closest("[data-qty-btn]");
  if (!btn) return;

  const input = btn.closest("[data-qty-wrapper]")?.querySelector("[data-qty-input]");
  if (!input) return;

  const currentVal = parseInt(input.value) || 1;
  const min = parseInt(input.min) || 0;
  const max = parseInt(input.max) || 99;

  if (btn.dataset.qtyBtn === "minus") {
    input.value = Math.max(min, currentVal - 1);
  } else if (btn.dataset.qtyBtn === "plus") {
    input.value = Math.min(max, currentVal + 1);
  }

  /* Trigger change event so CartDrawerItems picks it up */
  input.dispatchEvent(new Event("change", { bubbles: true }));
});


/* =========================================
   SECTION: Checkout Button Handler
   Navigates to checkout URL when clicked.
   Respects the disabled state set by gift sync.
========================================= */
document.addEventListener("click", (event) => {
  const btn = event.target.closest("[data-checkout-btn]");
  if (!btn || btn.hasAttribute("disabled")) return;

  event.preventDefault();
  CartLogger.logOperation("checkout");

  window.location.href = btn.dataset.checkoutUrl || "/checkout";
});
